import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../../services/api";
import Constants from "../../../services/constants";
import { showToast } from "../toastSlice";
import { hideLoader, showLoader } from "../loaderSlice";

const initialState = {
  settings: {
    email_notifications_enabled: true,
    notification_emails: [],
    slack_notifications_enabled: false,
    slack_webhook_url: "",
    slack_channel: "",
    notification_preferences: {
      alerts: true,
      reports: true,
      systemUpdates: false,
      maintenance: true,
    },
  },
  loading: false,
  error: null,
};

// Get notification settings
export const getNotificationSettings = createAsyncThunk(
  "notificationSettings/get",
  async (_, { dispatch, getState }) => {
    try {
      const org_id = getState().org?.active?.id;
      const res = await api("get", "notification-settings", {}, { org_id });
      if (res?.success) {
        return res.data;
      } else {
        dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      dispatch(
        showToast({
          severity: "error",
          summary: "Failed to fetch notification settings",
        })
      );
      return Promise.reject(error);
    } finally {
    }
  }
);

// Update notification settings
export const updateNotificationSettings = createAsyncThunk(
  "notificationSettings/update",
  async (data, { dispatch, getState }) => {
    try {
      dispatch(showLoader());
      const org_id = getState().org?.active?.id;
      const res = await api("put", "notification-settings", data, { org_id });
      if (res?.success) {
        dispatch(
          showToast({
            severity: "success",
            summary: "Notification settings updated successfully",
          })
        );
        return res.data;
      } else {
        dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      dispatch(
        showToast({
          severity: "error",
          summary: "Failed to update notification settings",
        })
      );
      return Promise.reject(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

// Test notification
export const testNotification = createAsyncThunk(
  "notificationSettings/test",
  async (testConfig, { dispatch, getState }) => {
    try {
      const { type } = testConfig;
      dispatch(showLoader());
      const org_id = getState().org?.active?.id;

      const res = await api("post", "notification-settings/test", testConfig, {
        org_id,
      });
      if (res?.success) {
        dispatch(
          showToast({
            severity: "success",
            summary: `Test ${type} notification sent successfully`,
          })
        );
        return res.data;
      } else {
        dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      const { type } = testConfig;
      dispatch(
        showToast({
          severity: "error",
          summary: `Failed to send test ${type} notification`,
        })
      );
      return Promise.reject(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

const notificationSettingsSlice = createSlice({
  name: "notificationSettings",
  initialState,
  reducers: {
    resetError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get settings
      .addCase(getNotificationSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getNotificationSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.settings = action.payload;
      })
      .addCase(getNotificationSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Update settings
      .addCase(updateNotificationSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateNotificationSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.settings = action.payload;
      })
      .addCase(updateNotificationSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Test notification
      .addCase(testNotification.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(testNotification.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(testNotification.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const { resetError } = notificationSettingsSlice.actions;

export default notificationSettingsSlice.reducer;
